import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, UserCircle, Archive, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import CustomerCommsButtons from "@/components/CustomerCommsButtons";

const PAGE_SIZE = 50;

export default function Customers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", address: "", latitude: null, longitude: null, notes: "", lead_source: "" });
  const nav = useNavigate();

  // 300ms debounce on search input to avoid hitting the server on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedQ, showArchived, sortBy, sortDir]);

  const load = async () => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    params.set("sort_by", sortBy);
    params.set("sort_dir", sortDir);
    if (showArchived) params.set("only_archived", "true");
    else if (debouncedQ) params.set("include_archived", "true");
    if (debouncedQ) params.set("q", debouncedQ);
    try {
      const { data } = await api.get(`/customers?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setInitialLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debouncedQ, showArchived, page, sortBy, sortDir]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setForm({ name: "", company: "", email: "", phone: "", address: "", latitude: null, longitude: null, notes: "", lead_source: "" });
      load();
      nav(`/app/customers/${data.id}`);
    } catch { toast.error("Failed"); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = items;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">CRM</div>
          <h1 className="font-heading text-4xl font-bold">Customers</h1>
        </div>
        <Button onClick={()=>setOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="customers-new-btn">
          <Plus className="w-4 h-4 mr-1"/> Add customer
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search name, email, phone" className="pl-9" data-testid="customers-search-input"/>
          </div>
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={`text-xs font-mono-accent uppercase tracking-widest px-3 py-2 rounded-md border flex items-center gap-1 transition-colors ${
              showArchived ? "border-[#FF5A00] text-[#FF5A00] bg-orange-50" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
            }`}
            data-testid="customers-toggle-archived"
          >
            <Archive className="w-3 h-3"/> {showArchived ? "Viewing archived" : "Show archived"}
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">
                <button onClick={() => toggleSort("name")} className="inline-flex items-center gap-1 hover:text-zinc-900" data-testid="customers-sort-name">
                  Name <ArrowUpDown className="w-3 h-3"/> {sortBy === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Address</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">
                <button onClick={() => toggleSort("created_at")} className="inline-flex items-center gap-1 hover:text-zinc-900" data-testid="customers-sort-created">
                  Added <ArrowUpDown className="w-3 h-3"/> {sortBy === "created_at" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {initialLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-t border-zinc-100" data-testid={`customers-skeleton-row-${i}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-100 animate-pulse"/>
                      <div className="min-w-0 flex-1">
                        <div className="h-3 w-32 bg-zinc-100 rounded animate-pulse"/>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-3 w-24 bg-zinc-100 rounded animate-pulse mb-1"/>
                    <div className="h-3 w-36 bg-zinc-100 rounded animate-pulse"/>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="h-3 w-40 bg-zinc-100 rounded animate-pulse"/>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="h-3 w-16 bg-zinc-100 rounded animate-pulse"/>
                  </td>
                </tr>
              ))
            ) : (
              <>
                {filtered.map((c) => (
                  <tr key={c.id} onClick={()=>nav(`/app/customers/${c.id}`)} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`customer-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-semibold">{(c.name||"?").slice(0,1).toUpperCase()}</div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          {c.company && <div className="text-[11px] text-zinc-500 truncate">{c.company}</div>}
                          {c.archived && <div className="text-[10px] uppercase tracking-widest text-zinc-400">Archived</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">
                      <div className="text-xs">{c.phone || "—"}</div>
                      <div className="text-xs text-zinc-500">{c.email || "—"}</div>
                      <div className="mt-1.5">
                        <CustomerCommsButtons customer={c} rowTestId={`customer-row-${c.id}`}/>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600 text-xs hidden md:table-cell">{c.address || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs hidden md:table-cell">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="4" className="text-center text-zinc-500 py-12" data-testid="customers-empty-state">
                    <UserCircle className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
                    No customers yet.
                  </td></tr>
                )}
              </>
            )}
          </tbody>
        </table>
        {/* Pagination footer */}
        <div className="px-4 py-3 border-t border-zinc-100 flex items-center gap-3 flex-wrap" data-testid="customers-pagination">
          <div className="text-xs text-zinc-500 font-mono-accent">
            {initialLoading
              ? <span className="inline-block h-3 w-24 bg-zinc-100 rounded animate-pulse" data-testid="customers-count-skeleton"/>
              : (total === 0 ? "0 results" : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`)}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-xs border border-zinc-200 rounded px-2 py-1 disabled:opacity-40 hover:border-zinc-400"
              data-testid="customers-prev-page"
            ><ChevronLeft className="w-3 h-3 inline"/> Prev</button>
            <div className="text-xs text-zinc-600 font-mono-accent">Page {page} / {totalPages}</div>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="text-xs border border-zinc-200 rounded px-2 py-1 disabled:opacity-40 hover:border-zinc-400"
              data-testid="customers-next-page"
            >Next <ChevronRight className="w-3 h-3 inline"/></button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="customer-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New customer</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="customer-form-name"/>
            </div>
            <div className="col-span-2">
              <Label>Company <span className="text-zinc-400 text-xs">(optional)</span></Label>
              <Input value={form.company} onChange={(e)=>setForm({...form, company: e.target.value})} placeholder="Acme Roofing LLC" data-testid="customer-form-company"/>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="customer-form-email"/>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="customer-form-phone"/>
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                onSelect={(p) => setForm({
                  ...form,
                  address: p.formatted,
                  latitude: p.lat,
                  longitude: p.lng,
                })}
                data-testid="customer-form-address"
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}/>
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="customer-form-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
